import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
  Snackbar,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import axios from 'axios';
import { useSubscriptionStore } from '../../store';

function ShareMaintenanceDialog({ open, handleClose, maintenance }) {
  const { t } = useTranslation();
  const { subscriptionInfo, fetchSubscriptionInfo, canUseSms } = useSubscriptionStore();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Share options
  const [sendEmail, setSendEmail] = useState(false);
  const [sendSms, setSendSms] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState(maintenance?.customer?.preferred_language || 'fi');

  // Result state
  const [result, setResult] = useState(null);

  // Check if SMS is available (not available during trial)
  const smsEnabled = canUseSms();
  const isTrial = subscriptionInfo?.is_trial;

  useEffect(() => {
    if (open && maintenance?.id) {
      fetchShareData();
      setLanguage(maintenance?.customer?.preferred_language || 'fi');
      // Fetch subscription info if not already loaded
      if (!subscriptionInfo) {
        fetchSubscriptionInfo();
      }
    }
  }, [open, maintenance?.id]);

  useEffect(() => {
    // Pre-fill customer contact info
    if (shareData) {
      setEmail(shareData.customer_email || '');
      setPhone(shareData.customer_phone || '');
    }
  }, [shareData]);

  const fetchShareData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/maintenances/${maintenance.id}/share/`);
      setShareData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('Error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareData?.public_url) return;

    const text = shareData.public_url;

    try {
      // Modern Clipboard API - works on all modern browsers including Chrome Android
      // Requires HTTPS and user gesture (button click satisfies this)
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers or when permissions denied
      console.log('Clipboard API failed, using fallback:', err);

      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const success = document.execCommand('copy');
        if (success) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }
      } catch (fallbackErr) {
        console.error('Copy failed:', fallbackErr);
      }

      document.body.removeChild(textArea);
    }
  };

  const handleSend = async () => {
    if (!sendEmail && !sendSms) return;

    setSending(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post(`/api/maintenances/${maintenance.id}/share/`, {
        send_email: sendEmail,
        send_sms: sendSms,
        email: email || undefined,
        phone: phone || undefined,
        language,
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('Error'));
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    setShareData(null);
    setError(null);
    setResult(null);
    setSendEmail(false);
    setSendSms(false);
    handleClose();
  };

  const handleOpenLink = () => {
    if (shareData?.public_url) {
      window.open(shareData.public_url, '_blank');
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            {t('Share with customer')}
          </Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: '8px' }}>
              {error}
            </Alert>
          ) : (
            <>
              {/* Explanation */}
              <Alert
                severity="info"
                icon={<LinkIcon />}
                sx={{
                  mb: 3,
                  borderRadius: '8px',
                  '& .MuiAlert-message': {
                    fontSize: '13px',
                  },
                }}
              >
                {t('Share tracking link info')}
              </Alert>

              {/* Public Link Section */}
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {t('Tracking link')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '13px',
                    color: 'text.secondary',
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {shareData?.public_url}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleOpenLink}
                  title={t('Open link')}
                  sx={{ color: 'text.secondary' }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCopyLink}
                  title={t('Copy link')}
                  sx={{
                    color: copied ? 'success.main' : 'text.secondary',
                  }}
                >
                  {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Send Options */}
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {t('Send to customer')}
              </Typography>

              {/* Language selector */}
              <LanguageSelect
                size="small"
                sx={{ mb: 2, minWidth: 200 }}
                value={language}
                label={t('Message language')}
                onChange={(e) => setLanguage(e.target.value)}
              />

              {/* Email Option */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      sx={{ color: 'secondary.main', '&.Mui-checked': { color: 'secondary.main' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Typography sx={{ fontSize: '14px' }}>{t('Send via email')}</Typography>
                    </Box>
                  }
                />
                {sendEmail && (
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    placeholder={t('Email address')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      mt: 1,
                      ml: 4,
                      width: 'calc(100% - 32px)',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                  />
                )}
              </Box>

              {/* SMS Option */}
              <Box sx={{ mb: 2 }}>
                <Tooltip
                  title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''}
                  placement="right"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendSms}
                        onChange={(e) => setSendSms(e.target.checked)}
                        disabled={!smsEnabled}
                        sx={{ color: 'secondary.main', '&.Mui-checked': { color: 'secondary.main' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmsIcon sx={{ fontSize: 20, color: smsEnabled ? 'text.secondary' : 'text.disabled' }} />
                        <Typography sx={{ fontSize: '14px', color: smsEnabled ? 'inherit' : 'text.disabled' }}>
                          {t('Send via SMS')}
                        </Typography>
                        {!smsEnabled && isTrial && (
                          <Tooltip title={t('SMS is not available during trial')}>
                            <InfoIcon sx={{ fontSize: 16, color: 'info.main', ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                </Tooltip>
                {!smsEnabled && isTrial && (
                  <Alert severity="info" sx={{ mt: 1, ml: 4, width: 'calc(100% - 32px)', borderRadius: '8px' }}>
                    {t('SMS notifications are available with a paid subscription. Upgrade to send SMS messages to customers.')}
                  </Alert>
                )}
                {sendSms && smsEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    type="tel"
                    placeholder={t('Phone number')}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    sx={{
                      mt: 1,
                      ml: 4,
                      width: 'calc(100% - 32px)',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                  />
                )}
              </Box>

              {/* Result Messages */}
              {result && (
                <Box sx={{ mt: 2 }}>
                  {result.email_sent && (
                    <Alert severity="success" sx={{ mb: 1, borderRadius: '8px' }}>
                      {t('Email sent successfully')}
                    </Alert>
                  )}
                  {result.email_error && (
                    <Alert severity="error" sx={{ mb: 1, borderRadius: '8px' }}>
                      {t('Email sending failed')}
                    </Alert>
                  )}
                  {result.sms_sent && (
                    <Alert severity="success" sx={{ mb: 1, borderRadius: '8px' }}>
                      {t('SMS sent successfully')}
                    </Alert>
                  )}
                  {result.sms_error && (
                    <Alert severity="error" sx={{ mb: 1, borderRadius: '8px' }}>
                      {t('SMS sending failed')}
                    </Alert>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCancel}
            sx={{
              borderRadius: '8px',
              px: 2.5,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 500,
            }}
          >
            {t('Close')}
          </Button>
          {(sendEmail || sendSms) && (
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={sending || (!sendEmail && !sendSms) || (sendEmail && !email) || (sendSms && !phone)}
              sx={{
                bgcolor: 'secondary.main',
                borderRadius: '8px',
                px: 2.5,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 500,
                '&:hover': {
                  bgcolor: 'secondary.dark',
                },
              }}
            >
              {sending ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                  {t('Sending...')}
                </>
              ) : (
                t('Send')
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message={t('Link copied to clipboard')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}

export default ShareMaintenanceDialog;
